<script setup lang="ts">
/**
 * This component is just for Jira-sync'd sessions
 */
import { useI18n } from "vue-i18n";

import { jqlQueryCopied } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import CopyButton from "@/components/ui/CopyButton/CopyButton.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import type { IssueSelectorJQLQuerySection } from "@/store/issueSelector";
import { useIssueSelectorStore } from "@/store/issueSelector";
import { relativeTime } from "@/utils/dateFormat";

import AdvancedFilterTooltip from "./AdvancedFilterTooltip.vue";

const store = useIssueSelectorStore();
const { t } = useI18n();

const getTooltipKey = (sectionName: string, isDefault: boolean) => {
  switch (sectionName) {
    case "text":
      return /*$t*/ "issueSelector.jql.text";
    case "text_and_issuekey":
      return /*$t*/ "issueSelector.jql.text_and_issuekey";
    case "assignee":
      return /*$t*/ "issueSelector.jql.assignee";
    case "project":
      return /*$t*/ "issueSelector.jql.project.default";
    case "sprint":
      return /*$t*/ "issueSelector.jql.sprint.default";
    case "team_fields":
      return /*$t*/ "issueSelector.jql.team_fields.default";
    case "issuetype":
      return isDefault
        ? /*$t*/ "issueSelector.jql.issuetype.default"
        : /*$t*/ "issueSelector.jql.issuetype";
    case "linked_to":
      return /*$t*/ "issueSelector.jql.linked_to";
    case "resolution":
      return /*$t*/ "issueSelector.jql.resolution.default";
    case "order_by":
      return /*$t*/ "issueSelector.jql.order_by.default";
    default:
      return /*$t*/ "issueSelector.jql.unknown";
  }
};

function jqlText() {
  if (!store.query) return "";
  const separator = store.query.separator.trim();
  return store.query.sections
    .map((section, index) => {
      if (index === 0) return section.value;
      const needsSeparator = showSeparatorBefore(section, index);
      return needsSeparator ? `${separator} ${section.value}` : section.value;
    })
    .join(" ");
}

const showSeparatorBefore = (section: IssueSelectorJQLQuerySection, index: number) => {
  return index > 0 && section.name !== "order_by";
};
</script>

<template>
  <DropdownMenu class="issue-advanced-filter" stay-open-on-content-click width="lg">
    <!-- Trigger Button -->
    <template #trigger="{ isOpen }">
      <IconButton
        icon="general/filter"
        variant="outlined"
        size="small"
        :activated="isOpen"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        :aria-label="$t('issueSelector.filter.advanced')"
      >
        <template #tooltip>
          <AdvancedFilterTooltip />
        </template>
      </IconButton>
    </template>

    <!-- Dropdown Content -->
    <div class="advanced-filter-content">
      <section>
        <h5 class="section-header">{{ $t("issueSelector.advanced.finalQuery") }}</h5>
        <!-- Query Block -->
        <div class="query-block">
          <!-- Loading Message -->
          <span v-if="!store.query">{{ $t("issueSelector.loading") }}</span>

          <template v-else>
            <!-- One 'section' per part of the filter -->
            <template
              v-for="(section, index) in store.query.sections"
              :key="`${section.name}-${section.isDefault}`"
            >
              <!-- Separator between sections -->
              <span v-if="showSeparatorBefore(section, index)" class="separator">
                {{ store.query.separator }}
              </span>
              <!-- JQL Section with Tooltip -->
              <BaseTooltip :content="t(getTooltipKey(section.name, section.isDefault))">
                <span class="query-section"> {{ section.value }} </span>
              </BaseTooltip>
            </template>

            <!-- Copy Icon -->
            <CopyButton
              :text="jqlText"
              class="copy-button"
              @copied="trackEvent(jqlQueryCopied())"
            />
          </template>
        </div>
      </section>

      <!-- Footer -->
      <div class="footer">
        <!-- Reload button -->
        <BaseButton
          size="small"
          variant="outlined"
          :disabled="store.isLoading"
          @click="store.fetchIssues"
        >
          {{ $t("issueSelector.advanced.reload") }}
        </BaseButton>

        <!-- Last reloaded timestamp -->
        <span v-if="store.lastReloadedAt" class="timestamp">
          {{
            $t("issueSelector.advanced.lastReloaded", { time: relativeTime(store.lastReloadedAt) })
          }}
        </span>
      </div>
    </div>
  </DropdownMenu>
</template>

<style lang="scss">
// Intentionally NOT scoped, because the dropdown (.issue-advanced-filter) has to be
// teleported to the body, otherwise some of the sections' tooltips won't render
// correctly. See https://gitlab.com/rentouch/pi-web/-/merge_requests/3316
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.issue-advanced-filter.base-popup {
  width: 560px !important;

  .advanced-filter-content {
    display: flex;
    flex-direction: column;
    padding: 20px;
    gap: 32px;

    .section-header {
      @include typography.small("semi-bold");

      margin-bottom: 8px;
    }

    .query-block {
      @include typography.small("medium");

      position: relative;
      display: flex;
      flex-wrap: wrap;
      column-gap: 3px;
      padding: 12px;
      color: colors.$text;
      line-height: 22px;
      background-color: colors.$background-grey;
      border-radius: 6px;

      > .copy-button {
        position: absolute;
        right: 8px;
        top: 8px;
        opacity: 0;
      }

      &:hover > .copy-button,
      &:focus-within > .copy-button {
        opacity: 1;
      }
    }

    // Individual pieces of query
    .query-section {
      color: colors.$text-subtle;
      border-bottom: 1.5px solid colors.$text-subtle;
      cursor: help;
    }

    // "AND" separator between JQL sections
    .separator {
      color: colors.$text-subtle;
    }

    .footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .timestamp {
      @include typography.small("medium");

      color: colors.$text-subtle;
    }
  }
}
</style>
